import { useEffect, useRef } from 'react';
import { HERO_CLIP, HERO_POSTER } from '../content/landing';
import { prefersReducedMotion } from '../lib/platform';
import { useSettings } from '../store/settings';


/** Data savers and very slow connections keep the still frame. */
function lightOnly(): boolean {
  const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  return !!c?.saveData || c?.effectiveType === '2g' || c?.effectiveType === 'slow-2g';
}

/**
 * The landing hero: a real match recorded from the game, shown as a still frame first. The video only
 * loads once the page has (it never competes with the page itself), plays while on screen, and stays a
 * still under reduced motion or a data saver.
 */
export function HeroClip() {
  const ref = useRef<HTMLVideoElement>(null);
  const reduceMotion = useSettings((s) => s.reduceMotion);

  useEffect(() => {
    const video = ref.current!;
    if (reduceMotion || prefersReducedMotion() || lightOnly()) return;
    let visible = true;
    const io = new IntersectionObserver(([e]) => {
      visible = !!e?.isIntersecting;
      if (!video.src) return;
      if (visible) void video.play().catch(() => {});
      else video.pause();
    });
    io.observe(video);
    const load = () => {
      video.src = HERO_CLIP;
      if (visible) void video.play().catch(() => {});
    };
    let idle = 0;
    const later = () => (idle = window.setTimeout(load, 300));
    if (document.readyState === 'complete') later();
    else window.addEventListener('load', later, { once: true });
    return () => {
      io.disconnect();
      window.removeEventListener('load', later);
      window.clearTimeout(idle);
      video.removeAttribute('src');
      video.load();
    };
  }, [reduceMotion]);

  return <video ref={ref} className="preview-canvas" poster={HERO_POSTER} width={720} height={720} muted loop playsInline preload="none" aria-hidden="true" />;
}
